// -----------------------------------------------------------------------------
// engine/compositor/layer.ts — draw a single clip's layer.
//
// Sits between the render plan and the low-level compositor. For Tier 0 it knows
// two kinds of layer:
//   • media — contain-fit a decoded frame/image into the project frame and draw,
//   • text  — render the overlay text to a 2D canvas, then draw it full-frame.
//
// Transform / crop / PiP are Tier 1; they will extend the media path here without
// touching gl.ts. (CLAUDE.md §4: "layer.ts — draw one clip".)
// -----------------------------------------------------------------------------
import { type ColorAdjust, NEUTRAL_ADJUST } from '../../core/model';
import { containRect } from '../../shared/geometry';
import type { TextLayer } from '../render-plan';
import type { Compositor } from './gl';

// Draw a decoded media frame, letterboxed to fit the frame, at the given opacity
// and colour grade.
export function drawMediaLayer(
  comp: Compositor,
  bitmap: ImageBitmap,
  opacity: number,
  adjust: ColorAdjust = NEUTRAL_ADJUST,
): void {
  const rect = containRect(bitmap.width, bitmap.height, comp.width, comp.height);
  comp.drawTexturedQuad(bitmap, rect, opacity, adjust);
}

// Render a text overlay. `scale` maps project-resolution px to the compositor's
// current render size (preview can be smaller than the project).
export function drawTextLayer(comp: Compositor, layer: TextLayer, scale: number): void {
  if (!layer.text) return;
  const { canvas, ctx } = comp.overlay2d();
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const fontPx = Math.max(1, layer.fontSize * scale);
  ctx.font = `bold ${fontPx}px ${layer.fontFamily || 'sans-serif'}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = layer.color || '#ffffff';
  // A soft shadow keeps text readable over busy video.
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur = Math.max(2, fontPx * 0.06);
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = Math.max(1, fontPx * 0.03);

  const cx = layer.xFrac * canvas.width;
  const cy = layer.yFrac * canvas.height;
  const lines = layer.text.split('\n');
  const lineHeight = fontPx * 1.2;
  const top = cy - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((line, i) => ctx.fillText(line, cx, top + i * lineHeight));

  comp.drawTexturedQuad(canvas, { x: 0, y: 0, w: comp.width, h: comp.height }, layer.opacity);
}
